import useCalendar from '@/hooks/calendar/useCalendar';
import useCards from '@/hooks/cards/useCards';
import useKeyValueStorage from '@/hooks/storage/useKeyValueStorage';
import { getFromAPI } from '@/lib/api';
import {
	CollectionAttributes,
	StrapiAPIResponseArray,
	StrapiEntry,
} from '@/lib/apiTypes';
import { useCallback, useEffect, useMemo } from 'react';
import useSWR, { preload } from 'swr';

preload('/collections', getFromAPI);

export const fullCollectionLength = 4;

const useCollections = () => {
	const { getCardBySpeciesId } = useCards();
	const { getIsSpeciesInCalendar } = useCalendar();

	// Store cached species in state
	const { value: cachedCollections, setValue: setCachedCollections } =
		useKeyValueStorage<
			StrapiAPIResponseArray<CollectionAttributes> | undefined
		>('collections', undefined);

	// Fetch from API
	const {
		data: remoteCollections,
		error,
		isLoading,
	} = useSWR<StrapiAPIResponseArray<CollectionAttributes>>(
		'/collections',
		getFromAPI,
		{
			revalidateOnReconnect: true,
			revalidateIfStale: false,
			revalidateOnFocus: true,
			revalidateOnMount: false,
			errorRetryCount: 3,
			fallbackData: cachedCollections,
		},
	);

	// Update cache if remote data is different
	useEffect(() => {
		if (
			remoteCollections &&
			JSON.stringify(remoteCollections) !==
				JSON.stringify(cachedCollections)
		) {
			setCachedCollections(remoteCollections);
		}
	}, [remoteCollections, cachedCollections, setCachedCollections]);

	// Return collections
	const collections =
		cachedCollections !== undefined ? cachedCollections : remoteCollections;

	// Collections map indexed by species ID
	const speciesIdToCollectionMap = useMemo(() => {
		const map = new Map<number, StrapiEntry<CollectionAttributes>[]>();
		if (!collections || !collections?.data) return map;
		collections.data.forEach(collection => {
			collection.attributes.speciesEntries.data?.forEach(species => {
				const speciesId = species.id;
				const speciesCollections = map.get(speciesId) ?? [];
				speciesCollections.push(collection);
				map.set(speciesId, speciesCollections);
			});
		});
		return map;
	}, [collections]);

	// Find collections of a specific species ID
	const findCollectionsOfSpeciesId = useCallback(
		(speciesId: number) => {
			return speciesIdToCollectionMap.get(speciesId) ?? [];
		},
		[speciesIdToCollectionMap],
	);
	// Get collections sorted by collected cards (most collected cards on top)
	// Exception: if collected cards is equal to fullCollectionLength, put the collection at the bottom
	const collectionsSortedByCollectedCards = useMemo(() => {
		if (!collections || !collections?.data) return [];
		return (
			collections.data
				.map(collection => ({
					...collection,
					collectedCards:
						collection.attributes.speciesEntries.data?.filter(
							collectionSpecies =>
								getCardBySpeciesId(collectionSpecies.id),
						).length ?? 0,
				}))
				// Sort them by count of species that are included in the calendar
				.sort((a, b) =>
					(a.attributes.speciesEntries.data?.filter(species =>
						getIsSpeciesInCalendar(species.id),
					).length ??
					0 >
						(b.attributes.speciesEntries.data?.filter(species =>
							getIsSpeciesInCalendar(species.id),
						).length ?? 0))
						? -1
						: 1,
				)
				// Sort by collected cards
				.sort((a, b) => b.collectedCards - a.collectedCards)
				// Put full collections at the bottom
				.sort((a, b) =>
					a.collectedCards === fullCollectionLength
						? 1
						: b.collectedCards === fullCollectionLength
							? -1
							: 0,
				)
		);
	}, [collections, getCardBySpeciesId, getIsSpeciesInCalendar]);

	return {
		collections,
		collectionsSortedByCollectedCards,
		findCollectionsOfSpeciesId,
		isLoading,
		error,
	};
};

export default useCollections;
